import {
  ChangeDetectorRef, ChangeDetectionStrategy, Component, ElementRef, ViewChild,
  NgZone, OnInit, OnDestroy, Input, SimpleChanges, OnChanges,
} from '@angular/core';
import { Router, ActivatedRoute, NavigationEnd, NavigationStart } from '@angular/router';
import { Location } from '@angular/common';
import { Observable } from 'rxjs';
import * as _ from 'lodash';



// data, models, helpers
import { ActiveFilterModel } from 'shared/search/active-filters/active-filter.model';
import { SearchModel } from 'shared/search/search.model';
import { StatusModel } from 'shared/search/status.model';
import { actions } from './actions';
import { PACT, SACT, LACT } from 'shared/fitments/contextual-actions/config';
import { columns } from 'shared/fitments/search/columns';
import { prettifyNumber } from 'utils/prettify';
import { getProductImageUrl } from 'shared/product/product-helpers';
import { LIST_FILTER_TYPE } from 'shared/search/active-filters/active-filter-types';
import { MAKE_TYPE, MODEL_TYPE, YEAR_START_TYPE, REGION_TYPE, VEHICLE_TYPE, SUB_MODEL_TYPE,
} from 'shared/search/advanced-search/filter-types';
import { createOption } from 'shared/form/select/option-helpers';
import { tooltipTexts } from 'shared/fitments/tooltip-texts';
import { myFitmentsOptions } from 'shared/fitments/fitments-options';
import { groupedAttributes } from 'shared/fitments/search/grouped-attributes';
import { indexPagesData } from 'utils/index-pages-data';
import { QueueProcess } from 'shared/process/queue-process.model';

// components
import { FitmentsSearchPageComponent } from 'shared/fitments/fitments-search-page.component';
import { FitmentPanelComponent } from '../fitment-panel/fitment-panel.component';
import { AddFitmentModalComponent } from '../add-fitment-modal/add-fitment-modal.component';
import { SearchInputComponent } from 'shared/search/search-input/search-input.component';

// services
import { SearchPageService } from 'shared/search-page/search-page.service';
import { ListManagerService } from 'shared/list-manager/list-manager.service';
import { FitmentsListService } from 'shared/fitments/fitments-list.service';
import { BrandService } from 'shared/brand/brand.service';
import { ProcessService } from 'shared/process/process.service';
import { ScrollToService } from 'shared/base/scroll-to.service';
import { MyFitmentsSearchService } from 'shared/fitments/search/my-fitments-search.service';
import { MyProductsStoreDataService } from 'shared/my-products-store-data/my-products-store-data.service';
import { AuthService } from 'shared/auth/auth.service';
import { LookupsService } from 'shared/lookups/lookups.service';
import { FitmentsService } from 'shared/fitments/fitments.service';
import { ItemDetailService } from 'shared/item-detail/item-detail.service';
import { PermissionsService } from 'shared/permissions/permissions.service';
import { SavedSearchesService } from 'shared/saved-searches/saved-searches.service';
import { UserService } from 'shared/user/user.service';
import { RightPanelService } from 'shared/right-panel/right-panel.service';
import { AppRoutingService } from '../../../../app-routing.service';
import {first, takeWhile} from 'rxjs/operators';
import {forkJoin} from 'rxjs/internal/observable/forkJoin';


@Component({
  moduleId: module.id,
  selector: 'fitments-search',
  templateUrl: 'fitments-search.component.html',
  styleUrls: ['fitments-search.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {provide: 'LIST_TYPE', useValue: 'fitment'},
    FitmentsListService,
  ],
})
export class FitmentsSearchComponent extends FitmentsSearchPageComponent implements OnInit, OnDestroy, OnChanges {

  @Input() itemDetail: any;
  @Input() editMode: boolean;
  @Input() scoreData: any;
  @Input() scorecardData: any;

  @ViewChild(FitmentPanelComponent, {static: false})
  fitmentPanel: FitmentPanelComponent | void;

  @ViewChild(AddFitmentModalComponent, {static: false})
  addFitmentModal: AddFitmentModalComponent | void;

  @ViewChild(SearchInputComponent, {static: false})
  searchInput: SearchInputComponent | void;

  showSidedrawer: boolean = false;
  placeholder: string = 'Search for Fitments assigned to this Item';
  actions: Array<any> = actions;
  ghostValue: string = 'fitments';
  editingRows: Array<any>; // Array for rows which should be edited without select them
  initialColumnsOrder: Array<string> = [
    'year', 'make', 'model', 'notes', 'mfrLabel', 'position', 'qty', 'partTerminologies',
    'qualifiers', 'digitalAssets', 'subModel', 'engineBase',
  ];
  // columns that must be in the end of the columns list by default
  lastColumns: Array<string> = [
    'qty', 'position', 'mfrLabel', 'notes', 'partTerminologies',
    'qualifiers', 'digitalAssets',
  ];
  isActive: boolean = true;
  isResearchMode: boolean = false;  // toggles between research and product
  searchModelStoreName: string = 'fitmentsSegmentSearchModel';
  procuctSearchModelStoreName: string = 'fitmentsSegmentSearchModelProduct';
  comingSoonColumns: Array<string> = [
    'digitalAssets',
  ];
  // after edit/remove fitment or column/assign fitment/replace fitment/ we save first revision id
  // to be able to rollback to the very first change via cancel button
  revisionId: number|string;
  private baseVehicleResearchRow: any;

  /*******************************************
  * COMPONENT GETTERS
  *******************************************/

  get showDefaultStateMessage(): boolean {
    if (this.searchResults.length) { return false; }
    if (this.searchModelFilters.length || this.searchModel.productId) {
      return false;
    }
    return true;
  }

  get noFitments(): boolean {
    return !this.searchResults.length;
  }

  get hasAdvancedSearch(): boolean {
    if (this.hasSearchFilters || this.isResearchMode) { return true; }
    if (this.productItemId) {
      return this.fitmentsLoaded ? !this.noFitments : true;
    } else {
      return true;
    }
  }

  get itemDetailId(): string {
    return _.get(this.itemDetail, '_id');
  }

  get disableResearch(): boolean {
    return !this.editMode || this.isResearchMode;
  }

  get disableEdit(): boolean {
    return !this.editMode || this.isResearchMode;
  }

  get editingRowsAppIds(): Array<any> {
    return _.map(this.editingRows, (item: any) => item.assignment_values.app_id);
  }

  get disableItemSelect(): boolean {
    return this.searchModel.selectAll === 'in-list' || !this.editMode;
  }

  get isPublished(): boolean {
    return _.includes(indexPagesData.published, _.get(this.itemDetail, 'status'));
  }

  get searchInputPlaceholder(): string {
    if (this.isResearchMode) {
      return `Research available fitments`;
    }
    return 'Search fitments assigned to this item';
  }

  get typeAheadValue(): any {
    return _.get(this.searchInput, 'typeAheadValue');
  }

  // change empty state title for invalid and duplicate status
  get noFitmentsTitle(): string|null {
    // invalid/duplicate
    if (this.isStatusInvalidOrDuplicate) {
      return 'Hooray!';
    }
    return 'This product needs fitments!';
  }

  // change empty state text for invalid and duplicate status
  get noFitmentsText(): string|null {
    if (this.currentStatusModelId === 2) {
      return 'There are no duplicate fitments.';
    } else if (this.currentStatusModelId === 1) {
      return 'There are no invalid fitments.';
    }
    return null;
  }

  get isStatusInvalidOrDuplicate(): boolean {
    return (this.currentStatusModelId === 1 || this.currentStatusModelId === 2);
  }

  get protectedColumns(): Array<string> {
    return _.get(this, 'searchService.protectedColumns');
  }

  constructor(
    public searchService: MyFitmentsSearchService,
    protected brandService: BrandService,
    protected pageService: SearchPageService,
    protected processService: ProcessService,
    protected scrollToService: ScrollToService,
    protected appRoutingService: AppRoutingService,
    protected listsService: FitmentsListService,
    protected permissionsService: PermissionsService,
    protected ngZone: NgZone,
    protected ref: ChangeDetectorRef,
    protected route: ActivatedRoute,
    protected location: Location,
    protected savedSearchesService: SavedSearchesService,
    protected router: Router,
    protected userService: UserService,
    protected fitmentsService: FitmentsService,
    private rightPanelService: RightPanelService,
  ) {
    super(
      searchService,
      brandService,
      pageService,
      processService,
      scrollToService,
      appRoutingService,
      listsService,
      permissionsService,
      ngZone,
      ref,
      route,
      location,
      savedSearchesService,
      router,
      userService,
      fitmentsService,
    );
  }

  /*******************************************
  * COMPONENT HOOKS
  *******************************************/

  ngOnChanges(changes: SimpleChanges) {
    if (changes.itemDetail && changes.itemDetail.currentValue && this.searchModel) {
      // reset revision id
      this.revisionId = null;
      // update fitments after itemDetail changes
      this.searchModel.productId = _.get(this.itemDetail, '_id');
      // after saving product page is set back to the view mode
      // if users are in the fitments research mode proceed them to the view mode
      if (this.isResearchMode) { this.onResearchToggle(); }
      this._searchModel.next(this.searchModel);
    }
    if (changes.editMode && !changes.editMode.currentValue) {
      if (_.size(this.selectedRows) || this.allSelected) { this.deselectAll(); }
    }
  }

  ngOnInit() {
    super.ngOnInit();
    this.searchService.updateColumnsEvent
    .pipe(takeWhile(() => this.isPageActive && !this.isResearchMode))
    .subscribe((_columns: any) => {
      // order columns if they were changed in the column builder
      let orderedColumns = _.sortBy(_columns, (columnName: string) => {
        const index = _.indexOf(this.searchModel.columns, columnName);
        // put new columns after fitment assign to the end
        if (index === -1) { return _columns.length; }
        return index;
      });
      if (!_.size(this.searchModel.columns)) {
        // SPD2-3869, Put QTY, Position Manf lable, and notes at the end columns list by default
        // if user has not changed them in the column builder
        _.pullAll(orderedColumns, this.lastColumns);
        orderedColumns = _.concat(orderedColumns, this.lastColumns);
      }
      const columnsData: any = this.getColumnsData(orderedColumns);
      this.searchResultsColumns = this.createNewColumns(columnsData);
      this.searchModel.columns = _.chain(this.searchResultsColumns).tail().map('columnName').value() as Array<string>;
    });
    this.searchResultsColumns = this.getDefaultColumns();
    this.resetSearchParams();
    if (this.itemDetail && this._searchModel) {
      this.searchModel.productId = _.get(this.itemDetail, '_id');
      this._searchModel.next(this.searchModel);
    }
  }

  ngOnDestroy() {
    this.isActive = false;
    super.ngOnDestroy();
  }

  /*******************************************
  * COMPONENT HANDLERS
  *******************************************/

  onPanelModalHide() {
    this.editingRows = null;
    this.searchService.clearSelectedItems();
    super.onPanelModalHide();
  }

  onResearchSaved() {
    this.showWheelLoader = false;
    this._searchModel.next(this.searchModel);
  }

  onFitmentsAssign(fitments: Array<any>) {
    this.startLoading();
    const assignmentValues = _.map(fitments, (item: any) => {
      const fitmentValues = _.pick(item, ['make_id', 'model_id', 'year']);
      _.set(fitmentValues, 'base_vehicle_id', item.id);
      return fitmentValues;
    });
    const requests: Array<Observable<any>> = [];
    requests.push(
      this.fitmentsService.assignToProduct(
        this.brandId,
        this.itemDetailId,
        assignmentValues,
        ['year', 'make', 'model']
      )
    );
    // if user perform research by invalid base vehicle, then remove invalid row
    if (this.baseVehicleResearchRow) {
      requests.push(
        this.fitmentsService.removeItemDetailApps(
          this.brandId,
          this.itemDetailId,
          [this.baseVehicleResearchRow.assignment_values.app_id]
        )
      );
      this.baseVehicleResearchRow = null;
    }
    forkJoin(requests)
    .pipe(
        first(),
        takeWhile(() => this.isPageActive)
    )
    .subscribe((result: Array<any>) => {
      this.setRevisionId(_.get(result[0], 'item_detail_revision_id'));
      this._searchModel.next(this.searchModel);
    }, () => {
      this.stopLoading();
    });
  }

  // Open fitment panel from search results by click on notes, mfrLabel, qty, or position
  onFitmentPanelOpen(row: any) {
    this.editingRows = _.concat(row);
    this.panelModalComponentIsShown = true;
    _.defer(() => this.fitmentPanel && this.fitmentPanel.show());
  }

  // Open YMM fitment modal after user researches by invalid base vehicle from search result
  onBaseVehicleResearch(row: any) {
    this.baseVehicleResearchRow = row;
    if (this.addFitmentModal instanceof AddFitmentModalComponent) {
      this.addFitmentModal.show();
    }
  }

  onActionSelected($event: any) {
    switch ($event.action) {
      case PACT.EDIT_SELECTED.action:
      case PACT.BULK_EDIT.action:
        this.panelModalComponentIsShown = true;
        _.defer(() => this.fitmentPanel && this.fitmentPanel.show());
        break;
      case PACT.ASSIGN_TO_PRODUCT.action:
        this.handleAssignToProduct();
        break;
      default:
        super.onActionSelected($event);
    }
  }

  onRemoveSelectedClick() {
    this.startLoading();
    let request: Observable<any>;
    if (this.allSelected) {
      request = this.fitmentsService.removeAllAppsInSearch(
        this.brandId,
        this.itemDetailId,
        this.searchService.getQueryString(this.searchModel)
      );
    } else {
      request = this.fitmentsService.removeItemDetailApps(this.brandId, this.itemDetailId, this.selectedAppIds);
    }
    request
    .pipe(takeWhile(() => this.isPageActive))
    .subscribe((res: any) => {
      this.deselectAll();
      this.stopLoading();
      this.setRevisionId(_.get(res, 'item_detail_revision_id'));
      if (res.processId) {
        const process: QueueProcess = new QueueProcess(res.processId, 0);
        this.startProcessingModal(process);
      } else {
        this._searchModel.next(this.searchModel);
      }
    });
  }

  onCancelContextualActionClick() {
    this.deselectAll();
    this._searchModel.next(this.searchModel);
  }

  onResearchToggle() {
    this.isResearchMode = !this.isResearchMode;
    if (this.isResearchMode) {
      // right panel not available in research mode
      this.toggleRightPanel(true);
    } else {
      // list manager not available in product mode
      this.showLists = false;
    }
    // reset all search model params
    // create new search model or get it from localStorage
    // get default columns or get them from localStorage
    // fetch fitments from the backend
    this.resetSearchParams();
    if (this.storedSearchModel)  {
      this.searchModel = this.storedSearchModel;
    } else if (!this.searchModel) {
      this.searchModel = new SearchModel();
    }
    if (this.storedFitmentsColumns) {
      this.searchResultsColumns = this.storedFitmentsColumns;
    } else {
      this.searchResultsColumns = this.getDefaultColumns();
    }
    if (!this.isResearchMode || this.searchModelFilters.length) {
      this.fitmentsLoaded = false;
      _.defer(() => this._searchModel.next(this.searchModel));
    }
  }

  onColumnBuilderShow() {
    this.showColumnBuilder();
  }

  onSearchResultsContainerChanged() {
    // update sorting panel to match search results columns changes
    if (this.sortingPanel) {
      this.sortingPanel.detectChanges();
    }
  }

  onFitmentsEditChangesSaved() {
    this.showWheelLoader = false;
    this._searchModel.next(this.searchModel);
  }


  onRevisionIdChanged(revisionId: string|number) {
    this.setRevisionId(revisionId);
  }

  onQueueProcessShow(processId: string|number) {
    const process: QueueProcess = new QueueProcess(processId, 0);
    this.startProcessingModal(process);
  }

  /*******************************************
  * COMPONENT PUBLIC
  *******************************************/

  changeColumns(selectedColumns: Array<any>): void {
    // Hide remove columns functionality for the time being
    // let removedColumns;
    // if (!this.isResearchMode) {
    //   // find columns that were unchecked in the column builder
    //   // filter not removable columns
    //   let activeColumnNames = _.compact(_.map(this.searchResultsColumns, 'columnName'));
    //   let selectedColumnNames = _.chain(selectedColumns)
    //   .map('columnName')
    //   .map((name: string) => {
    //     // replace group name on column names from that group
    //     const columnGroup = _.find(this.groupedColumns, ['option', name]);
    //     if (columnGroup) {
    //       return columnGroup.attributes;
    //     } else {
    //       return name;
    //     }
    //   }).flatten().value();
    //   removedColumns = _.chain(activeColumnNames)
    //   .difference(selectedColumnNames)
    //   .filter((name: string) => !_.includes(this.productNotRemovableColumns, name))
    //   .value();
    //   // console.log(removedColumns);
    // }

    // create new columns
    super.changeColumns(selectedColumns);

    if (this.isResearchMode) {
      // in research mode we don't run search if user doesn't apply any filters
      this.deselectAll();
      if (this.searchModelFilters.length) {
        this._searchModel.next(this.searchModel);
      }
    } else {
      this._searchModel.next(this.searchModel);
      // remove unchecked columns before fetching new fitments
      // Hide remove columns functionality for the time being
      // if (_.size(removedColumns)) {
      //   this.fitmentsService.removeColumnsFromApp(this.brandId, this.itemDetailId, removedColumns)
      //   .takeWhile(() => this.isPageActive)
      //   .subscribe(($event: any) => {
      //     this.setRevisionId(_.get($event, 'success[1]'));
      //     this._searchModel.next(this.searchModel);
      //   });
      // } else {
      //   this._searchModel.next(this.searchModel);
      // }
    }
  }

  showActionPanel(): boolean {
    return super.showActionPanel() && this.editMode;
  }

  toggleRightPanel(toggle?: boolean): void {
    this.rightPanelService.toggle(toggle);
  }

  // replace parent method:
  rewriteUrl(searchModel: SearchModel, _columns: Array<{}>) {
    if (this.isResearchMode) { return; }
    super.rewriteUrl(searchModel, _columns);
  }

  getCurrentRouteUrl(): string {
    return location.pathname;
  }

  public filterByStatus(status: StatusModel): void {
    this.searchModel.currentPage = 1;
    this.searchModel.statusModel = status;
    this._searchModel.next(this.searchModel);
  }

  public resetRevisionId(): void {
    this.revisionId = null;
  }

  public fetchFitments(): void {
    this._searchModel.next(this.searchModel);
  }

  /*******************************************
  * COMPONENT PRIVATE
  *******************************************/

  private handleAssignToProduct(): void {
    this.startLoading();
    this.assignToProductsRequest([this.itemDetail._id])
    .pipe(
        first(),
        takeWhile(() => this.isPageActive)
    )
    .subscribe((res: any) => {
      if (_.has(res, '[0].item_detail_revision_id')) {
        this.setRevisionId(_.get(res, '[0].item_detail_revision_id'));
      } else if (_.has(res, 'item_detail_revision_id')) {
        this.setRevisionId(_.get(res, 'item_detail_revision_id'));
      }
      this.deselectAll();
      this.stopLoading();
      if (res.processId) {
        const process: QueueProcess = new QueueProcess(res.processId, 0);
        this.startProcessingModal(process);
      }
    });
  }

  /*
    * Saves revision id of very first change
   */
  private setRevisionId(revisionId: number|string): void {
    if (!this.revisionId) { this.revisionId = revisionId; }
  }

}
